import { createContext, useContext, useState, useEffect } from 'react';
import i18n from '@/i18n';

// Supported languages with their native names
export const LANGUAGES = [
  { code: 'en', name: 'English', native: 'English', flag: '🇺🇸' },
  { code: 'es', name: 'Spanish', native: 'Espanol', flag: '🇪🇸' },
  { code: 'fr', name: 'French', native: 'Francais', flag: '🇫🇷' },
  { code: 'de', name: 'German', native: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', native: 'Italiano', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', native: 'Portugues', flag: '🇧🇷' },
  { code: 'zh', name: 'Chinese', native: '中文', flag: '🇨🇳' },
  { code: 'ja', name: 'Japanese', native: '日本語', flag: '🇯🇵' },
  { code: 'ko', name: 'Korean', native: '한국어', flag: '🇰🇷' },
  { code: 'ar', name: 'Arabic', native: 'العربية', flag: '🇸🇦' },
  { code: 'hi', name: 'Hindi', native: 'हिन्दी', flag: '🇮🇳' },
  { code: 'ru', name: 'Russian', native: 'Русский', flag: '🇷🇺' },
];

const LanguageContext = createContext(undefined);

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first, then browser preference
    const stored = localStorage.getItem('bittrade-language');
    if (stored) return stored;
    
    // Detect browser language
    const browserLang = navigator.language?.split('-')[0] || 'en';
    const supported = LANGUAGES.find(l => l.code === browserLang);
    return supported ? browserLang : 'en';
  });

  const setLanguage = (code) => {
    const lang = LANGUAGES.find(l => l.code === code);
    if (lang) {
      setLanguageState(code);
      localStorage.setItem('bittrade-language', code);
      
      // Sync with i18next
      i18n.changeLanguage(code);
      
      // Update document lang attribute
      document.documentElement.lang = code;
      
      // Update dir for RTL languages
      document.documentElement.dir = code === 'ar' ? 'rtl' : 'ltr';
    }
  };

  useEffect(() => {
    // Set initial document attributes
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
  }, [language]);

  const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      currentLanguage,
      languages: LANGUAGES,
      isRTL: language === 'ar'
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export default LanguageContext;
